import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckCircleOutline,
  ErrorOutline,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { z } from "zod";

import {
  getNationalLengthForCountry,
  requiresStrictNationalLength,
  sanitizePhoneDigits,
  toE164,
  trimPhoneToNationalLength,
} from "../../../utils/CountryPhoneUtils";
import { alphaOnly } from "../../../utils/InputValidation";
import { type Country } from "../../../utils/countries";
import {
  getRegisterSchema,
} from "../../../validation/registerSchema";
import i18n from "../../../config/i18n";
import CountryCodeSelect from "../../common/CountryCodeSelect";

/** Custom helper text component with error icon */
const ErrorHelperText: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 0.5,
        color: "error.main",
      }}
    >
      <ErrorOutline sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ color: "error.main" }}>
        {message}
      </Typography>
    </Box>
  );
};

/** Extend the base schema via intersection (works with ZodEffects) */
const getFormSchema = () => {
  const t = (key: string) => i18n.t(key);
  return getRegisterSchema().and(
    z
      .object({
        countryCode: z.string().min(2).max(3), // e.g., "US", "TJ"
        phoneNumber: z.string(),
        acceptTerms: z.boolean().refine((v) => v === true, {
          message: t("validation.acceptTerms"),
        }),
        referralCode: z.string().max(20).optional().or(z.literal("")),
      })
      .superRefine((data, ctx) => {
        if (!requiresStrictNationalLength(data.countryCode)) {
          return;
        }

        const digits = sanitizePhoneDigits(data.phoneNumber);
        if (!digits) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation.phoneRequired"),
            path: ["phoneNumber"],
          });
          return;
        }

        const expectedLength = getNationalLengthForCountry(data.countryCode);
        if (digits.length !== expectedLength) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("validation.phoneInvalid"),
            path: ["phoneNumber"],
          });
        }
      })
  );
};

type RegisterFormData = z.infer<ReturnType<typeof getFormSchema>>;

interface ManualRegisterFormProps {
  initialValues?: {
    email?: string;
    firstName?: string;
    lastName?: string;
  };
  onSubmit: (data: RegisterFormData) => Promise<unknown> | void;
  isLoading?: boolean;
  error?: string | null;
  onLogin?: () => void;
}

const ManualRegisterForm: React.FC<ManualRegisterFormProps> = ({
  initialValues = {},
  onSubmit,
  isLoading = false,
  error = null,
  onLogin,
}) => {
  const { t: myTranslate } = useTranslation();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const referralCodeFromUrl = searchParams.get("ref") || "";

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, touchedFields, isSubmitted, isValid },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(getFormSchema()),
    defaultValues: {
      email: initialValues.email || "",
      firstName: initialValues.firstName || "",
      lastName: initialValues.lastName || "",
      countryCode: "TJ",
      phoneNumber: "",
      password: "",
      acceptTerms: false,
      referralCode: referralCodeFromUrl,
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const countryCode = watch("countryCode");
  const maxLen = getNationalLengthForCountry(countryCode);

  const handleCountrySelect = (country: Country | null) => {
    const code = country?.code ?? "US";

    // IMPORTANT: do NOT validate when changing country (prevents empty-field error flash)
    setValue("countryCode", code, { shouldDirty: true, shouldValidate: false });

    // Trim phone to new max length, still no validation here
    const trimmed = trimPhoneToNationalLength(code, watch("phoneNumber") || "");
    setValue("phoneNumber", trimmed, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const handleFormSubmit = async (data: RegisterFormData) => {
    // Check phone validation before submitting
    if (requiresStrictNationalLength(data.countryCode)) {
      const digits = sanitizePhoneDigits(data.phoneNumber);
      if (!digits) {
        setValue("phoneNumber", "", { shouldValidate: true });
        return;
      }

      const expectedLength = getNationalLengthForCountry(data.countryCode);
      if (digits.length !== expectedLength) {
        setValue("phoneNumber", data.phoneNumber, { shouldValidate: true });
        return;
      }
    }

    // Convert to E.164 for the API
    const e164 = toE164(data.countryCode, data.phoneNumber);
    const payload = { ...data, phoneNumber: e164, confirmPassword: data.password };

    // Clear any previous success state
    setSuccessEmail(null);
    setSubmittedEmail(data.email);

    try {
      await onSubmit(payload as RegisterFormData);
      // Don't set success here - wait for useEffect to check error state
    } catch (err) {
      // Error will be surfaced via the error prop
      setSuccessEmail(null);
      setSubmittedEmail(null);
    }
  };

  // Watch for when submission completes and there's no error
  useEffect(() => {
    if (submittedEmail && !error && !isLoading) {
      // Only show success if we have submitted email, no error, and not loading
      setSuccessEmail(submittedEmail);
    } else if (error) {
      // Clear success if there's an error
      setSuccessEmail(null);
    }
  }, [error, isLoading, submittedEmail]);

  // Only show phone error after user has finished typing (blur) or after form submission
  const phoneErrorVisible =
    !!errors.phoneNumber && (touchedFields.phoneNumber || isSubmitted);

  // --- Success screen -------------------------------------------------------
  if (successEmail) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 2,
          py: 4,
        }}
      >
        <CheckCircleOutline color="success" sx={{ fontSize: 64 }} />
        <Typography
          variant="h5"
          className="notranslate"
          translate="no"
          sx={{ fontWeight: 700 }}
        >
          {myTranslate("auth.verifyYourEmail")}
        </Typography>
        <Typography
          className="notranslate"
          translate="no"
          sx={{ color: "text.secondary", maxWidth: 520 }}
        >
          {myTranslate("auth.verificationEmailSent", { email: successEmail })}
        </Typography>

        {onLogin && (
          <Button
            variant="contained"
            color="primary"
            className="notranslate"
            translate="no"
            onClick={onLogin}
            sx={{ mt: 1.5, fontWeight: 600, px: 4 }}
          >
            {myTranslate("auth.signIn")}
          </Button>
        )}

        <Typography
          variant="body2"
          className="notranslate"
          translate="no"
          sx={{ color: "text.secondary", mt: 1 }}
        >
          {myTranslate("auth.didntGetEmail")}
        </Typography>
      </Box>
    );
  }

  // --- Registration form ----------------------------------------------------
  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(handleFormSubmit)();
      }}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
      noValidate
    >
      <Box>
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                label={myTranslate("auth.firstName")}
                className="notranslate"
                translate="no"
                autoComplete="given-name"
                fullWidth
                required
                error={!!errors.firstName}
                disabled={isLoading}
                onChange={(e) =>
                  field.onChange(alphaOnly(e.target.value || ""))
                }
              />
              <ErrorHelperText message={errors.firstName?.message} />
            </>
          )}
        />
      </Box>

      <Box>
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                label={myTranslate("auth.lastName")}
                className="notranslate"
                translate="no"
                autoComplete="family-name"
                fullWidth
                required
                error={!!errors.lastName}
                disabled={isLoading}
                onChange={(e) =>
                  field.onChange(alphaOnly(e.target.value || ""))
                }
              />
              <ErrorHelperText message={errors.lastName?.message} />
            </>
          )}
        />
      </Box>

      <Box>
        <TextField
          label={myTranslate("auth.email")}
          className="notranslate"
          translate="no"
          {...register("email")}
          fullWidth
          required
          type="email"
          autoComplete="email"
          error={!!errors.email}
          disabled={isLoading}
        />
        <ErrorHelperText message={errors.email?.message} />
      </Box>

      {/* PHONE uses Controller so RHF stays the single source of truth */}
      <Controller
        name="phoneNumber"
        control={control}
        render={({ field }) => (
          <Box>
            <TextField
              {...field}
              label={myTranslate("auth.phoneNumber")}
              className="notranslate"
              translate="no"
              fullWidth
              required
              autoComplete="tel-national"
              error={phoneErrorVisible}
              disabled={isLoading}
              onChange={(e) => {
                const trimmed = trimPhoneToNationalLength(
                  countryCode,
                  e.target.value || ""
                );
                field.onChange(trimmed);
              }}
              // keep onBlur from RHF so touchedFields is set
              onBlur={field.onBlur}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <CountryCodeSelect
                      value={countryCode}
                      onChange={handleCountrySelect}
                      name="countryCode"
                    />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: maxLen,
              }}
            />
            <ErrorHelperText
              message={
                phoneErrorVisible ? errors.phoneNumber?.message : undefined
              }
            />
            {!phoneErrorVisible && (
              <Typography
                variant="caption"
                className="notranslate"
                translate="no"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {myTranslate("auth.phoneVerificationNote")}
              </Typography>
            )}
          </Box>
        )}
      />

      <Box>
        <TextField
          label={myTranslate("auth.password")}
          className="notranslate"
          translate="no"
          {...register("password")}
          fullWidth
          required
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          error={!!errors.password}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <ErrorHelperText message={errors.password?.message} />
        {!errors.password && (
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 0.5 }} className="notranslate" translate="no">
            {myTranslate("validation.passwordMinSimple")}
          </Typography>
        )}
      </Box>

      <input type="hidden" {...register("referralCode")} />

      <FormControlLabel
        control={<Checkbox {...register("acceptTerms")} color="primary" />}
        label={
          <Typography variant="body2" className="notranslate" translate="no">
            {myTranslate("auth.iAgreeToThe")}{" "}
            <Link
              component={RouterLink}
              to="/terms"
              target="_blank"
              color="inherit"
              underline="always"
            >
              {myTranslate("auth.termsOfService")}
            </Link>{" "}
            {myTranslate("auth.and")}{" "}
            <Link
              component={RouterLink}
              to="/privacyPolicy"
              target="_blank"
              color="inherit"
              underline="always"
            >
              {myTranslate("auth.privacyPolicy")}
            </Link>
          </Typography>
        }
      />
      {errors.acceptTerms && (
        <Typography
          variant="caption"
          color="error"
          className="notranslate"
          translate="no"
          sx={{ ml: 2 }}
        >
          {myTranslate("auth.pleaseAcceptTerms")}
        </Typography>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        className="notranslate"
        translate="no"
        disabled={isLoading || !isValid}
        sx={{ fontWeight: 600, py: 1.2 }}
      >
        {isLoading
          ? myTranslate("auth.creatingAccount")
          : myTranslate("auth.createAccountButton")}
      </Button>
    </Box>
  );
};

export default ManualRegisterForm;
